import type { RingBuffer } from './ringBuffer'
import type { ErrorEntry } from '../types'
import { scrubCredentials } from './scrub'

// Errors that bubble up to `window.error` or `unhandledrejection` often
// carry credentials in plain text — e.g. an SDK throws `new Error("Bearer
// eyJ... rejected (401)")`, or a promise rejects with `{apiKey: "sk_live_…"}`.
// Both ship to the central backend unless we scrub here.

export function installErrorHandlers(buffer: RingBuffer<ErrorEntry>): () => void {
  if (typeof window === 'undefined') return () => {}
  const onError = (e: ErrorEvent) => {
    const rawStack = e.error instanceof Error ? e.error.stack : undefined
    const stack = rawStack !== undefined ? scrubCredentials(rawStack) : undefined
    buffer.push({
      message: scrubCredentials(e.message || 'Unknown error'),
      ...(stack !== undefined && { stack }),
      ts: Date.now(),
      source: 'window.error',
    })
  }
  const onRejection = (e: PromiseRejectionEvent) => {
    const reason = e.reason
    const rawMessage =
      reason instanceof Error ? reason.message :
      typeof reason === 'string' ? reason :
      (() => { try { return JSON.stringify(reason) } catch { return String(reason) } })()
    const rawStack = reason instanceof Error ? reason.stack : undefined
    const stack = rawStack !== undefined ? scrubCredentials(rawStack) : undefined
    buffer.push({
      message: scrubCredentials(rawMessage),
      ...(stack !== undefined && { stack }),
      ts: Date.now(),
      source: 'unhandledrejection',
    })
  }
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}
